/**
 * AdminDatevAutoSendPage — Admin-Übersicht DATEV-Auto-Versand-Status.
 *
 * Route: /admin/datev/auto-send
 *
 * Funktionen:
 *   - Liste aller Händler mit Auto-Send-Status, Steuerberater-Mail,
 *     Beraternr./Mandantennr., letztem Versand und Anzahl bereits versendeter Perioden
 *   - Filter „nur aktive" (eligible für Cron)
 *   - Volltext-Suche (email/company/tax_advisor_email)
 *   - „Cron jetzt manuell auslösen"-Button
 *
 * Backend:
 *   - GET /api/admin/datev/auto-send-status
 *   - POST /api/admin/datev/trigger-monthly
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    ShieldCheck, Search, FileText, Play, AlertTriangle,
    CheckCircle2, Calendar, Loader2,
} from "lucide-react";

import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "—";

export default function AdminDatevAutoSendPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [onlyActive, setOnlyActive] = useState(false);
    const [triggering, setTriggering] = useState(false);
    const [triggerResult, setTriggerResult] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.set("q", search);
            if (onlyActive) params.set("only_active", "true");
            const r = await api.get(`/admin/datev/auto-send-status?${params}`);
            setItems(r.data?.items || []);
            setStats(r.data?.stats || {});
        } catch (e) {
            toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
        }
        setLoading(false);
    }, [search, onlyActive]);

    useEffect(() => {
        if (user === false) navigate("/login");
        if (user && user.role !== "admin") navigate("/");
        if (user) load();
    }, [user, navigate, load]);

    const trigger = async () => {
        if (!window.confirm("Monats-Auto-Versand jetzt auslösen? Bei aktivem Resend werden echte Mails versendet.")) return;
        setTriggering(true);
        try {
            const r = await api.post("/admin/datev/trigger-monthly");
            setTriggerResult(r.data);
            toast.success(`Cron-Lauf: ${r.data.summary.sent_count} versendet, ${r.data.summary.skipped_count} übersprungen`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Cron-Trigger fehlgeschlagen");
        }
        setTriggering(false);
    };

    if (!user) return <div className="p-8">Lade…</div>;

    return (
        <div className="min-h-screen bg-gray-50" data-testid="admin-datev-page">
            <div className="bg-[#111827] text-white">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
                    <Link to="/admin/dealers" className="text-xs text-gray-400 hover:text-white" data-testid="back-to-admin">
                        ← Admin-Dashboard
                    </Link>
                    <div className="flex items-end justify-between flex-wrap gap-3 mt-3">
                        <div>
                            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight inline-flex items-center gap-2">
                                <ShieldCheck className="w-7 h-7" /> DATEV-Auto-Versand-Status
                            </h1>
                            <p className="text-sm text-gray-400 mt-1">
                                Übersicht aller Händler-Konfigurationen für den monatlichen DATEV-Belegtransfer-Auto-Versand.
                            </p>
                        </div>
                        <Button
                            onClick={trigger}
                            disabled={triggering}
                            className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider"
                            data-testid="admin-datev-trigger-btn"
                        >
                            {triggering ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Triggere…</> : <><Play className="w-4 h-4 mr-1" /> Cron jetzt auslösen</>}
                        </Button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-4">
                    <StatCard label="Händler gesamt" value={stats.total_dealers} icon={<ShieldCheck className="w-5 h-5" />} />
                    <StatCard label="Auto-Send aktiv" value={stats.auto_send_enabled} highlight="emerald" icon={<CheckCircle2 className="w-5 h-5" />} testid="stat-active" />
                    <StatCard label="Cron-eligible" value={stats.eligible_for_cron} highlight="amber" icon={<Calendar className="w-5 h-5" />} testid="stat-eligible" />
                    <StatCard label="Versendete Perioden" value={stats.total_auto_periods_sent} highlight="blue" icon={<FileText className="w-5 h-5" />} />
                </div>

                {/* Trigger-Result */}
                {triggerResult && (
                    <div className="bg-emerald-50 border border-emerald-300 rounded-sm p-3 mb-4 text-sm" data-testid="admin-datev-trigger-result">
                        <div className="font-bold mb-1">Letzter Cron-Lauf · Periode {triggerResult.period}</div>
                        <div className="text-xs grid grid-cols-2 md:grid-cols-3 gap-x-4">
                            <div>✅ Versendet: <strong>{triggerResult.summary.sent_count}</strong></div>
                            <div>⊘ Übersprungen (keine Rechnungen): <strong>{triggerResult.summary.skipped_count}</strong></div>
                            <div>❌ Fehler: <strong>{triggerResult.summary.failed_count}</strong></div>
                        </div>
                        {triggerResult.sent.length > 0 && (
                            <details className="mt-2 text-xs">
                                <summary className="cursor-pointer text-emerald-700 font-bold">Details ({triggerResult.sent.length} versendet)</summary>
                                <ul className="mt-1 ml-4 list-disc">
                                    {triggerResult.sent.map((s) => (
                                        <li key={s.id}>{s.email} {s.mocked && <span className="text-amber-700">(MOCKED)</span>}</li>
                                    ))}
                                </ul>
                            </details>
                        )}
                    </div>
                )}

                {/* Filter */}
                <div className="bg-white border border-gray-200 rounded-sm p-3 mb-4 flex flex-wrap gap-2 items-center">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Suche: Email / Firma / Steuerberater-Mail …"
                            className="pl-8 rounded-sm"
                            data-testid="admin-datev-search"
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} data-testid="admin-datev-filter-active" />
                        Nur aktive (Auto-Send ein)
                    </label>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-12 text-center text-gray-500">Lade Dealer-Liste…</div>
                ) : items.length === 0 ? (
                    <div className="bg-white border border-gray-200 rounded-sm p-12 text-center text-gray-500" data-testid="admin-datev-empty">
                        Keine Dealer gefunden.
                    </div>
                ) : (
                    <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto" data-testid="admin-datev-table">
                        <table className="w-full text-sm">
                            <thead className="bg-[#111827] text-white text-[10px] uppercase tracking-wider">
                                <tr>
                                    <th className="text-left p-2">Händler</th>
                                    <th className="text-left p-2">Steuerberater</th>
                                    <th className="text-center p-2">Auto-Send</th>
                                    <th className="text-left p-2">DATEV-Nr.</th>
                                    <th className="text-left p-2">Letzter Versand</th>
                                    <th className="text-right p-2">Perioden</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((d) => (
                                    <DealerRow key={d.id} d={d} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

function StatCard({ label, value, icon, highlight, testid }) {
    const bg = highlight === "emerald" ? "border-emerald-400 bg-emerald-50"
        : highlight === "amber" ? "border-amber-400 bg-amber-50"
        : highlight === "blue" ? "border-blue-400 bg-blue-50"
        : "border-gray-200 bg-white";
    return (
        <div className={`border rounded-sm p-3 flex items-start gap-2 ${bg}`} data-testid={testid}>
            <div className="text-gray-600 mt-0.5">{icon}</div>
            <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600">{label}</div>
                <div className="font-display font-black text-2xl font-mono-num">{value ?? "—"}</div>
            </div>
        </div>
    );
}

function DealerRow({ d }) {
    const periods = d.datev_auto_sent_periods || [];
    const active = !!d.datev_auto_send_enabled;
    const eligible = active && !!d.tax_advisor_email;
    return (
        <tr className={`border-b border-gray-100 hover:bg-gray-50 ${!active ? "text-gray-500" : ""}`} data-testid={`dealer-row-${d.id}`}>
            <td className="p-2">
                <div className="font-bold">{d.company_name || d.name || "—"}</div>
                <div className="text-[11px] text-gray-500 font-mono-num">{d.email}</div>
                {d.city && <div className="text-[10px] text-gray-400">{d.city}</div>}
            </td>
            <td className="p-2 font-mono-num text-[11px]">{d.tax_advisor_email || <span className="text-gray-300">—</span>}</td>
            <td className="p-2 text-center">
                {eligible ? (
                    <Badge className="bg-emerald-100 text-emerald-800 text-[10px] uppercase rounded-sm font-bold" title="Cron-eligible">✓ Aktiv</Badge>
                ) : active ? (
                    <Badge className="bg-amber-100 text-amber-800 text-[10px] uppercase rounded-sm font-bold" title="Aktiv aber keine Steuerberater-Mail">⚠ Unvollständig</Badge>
                ) : (
                    <Badge className="bg-gray-100 text-gray-600 text-[10px] uppercase rounded-sm font-bold">Aus</Badge>
                )}
            </td>
            <td className="p-2 text-[11px] font-mono-num">
                {d.datev_consultant_number || d.datev_client_number ? (
                    <>
                        {d.datev_consultant_number && <div>B: {d.datev_consultant_number}</div>}
                        {d.datev_client_number && <div>M: {d.datev_client_number}</div>}
                    </>
                ) : <span className="text-gray-300">—</span>}
            </td>
            <td className="p-2 text-[11px]">
                {d.datev_last_sent_at ? (
                    <>
                        <div className="font-bold">{fmtDate(d.datev_last_sent_at)}</div>
                        {d.datev_last_sent_to && <div className="text-gray-500 font-mono-num">{d.datev_last_sent_to}</div>}
                        {d.datev_last_sent_count !== undefined && (
                            <div className="text-[10px] text-gray-400">
                                {d.datev_last_sent_count} Rg. {d.datev_last_sent_source && `· ${d.datev_last_sent_source}`}
                            </div>
                        )}
                    </>
                ) : <span className="text-gray-300">noch nie</span>}
            </td>
            <td className="p-2 text-right">
                {periods.length > 0 ? (
                    <span className="inline-block bg-blue-100 text-blue-800 px-2 py-0.5 rounded-sm font-bold text-[11px] font-mono-num" title={periods.join(", ")}>
                        {periods.length}
                    </span>
                ) : <span className="text-gray-300">0</span>}
            </td>
        </tr>
    );
}
